import { useState, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { useDebounce } from "../../shared/hooks/use-debounce";

export interface SearchUser {
  id: string;
  username: string;
  full_name: string | null;
  avatar_url: string | null;
  similarity: number;
  is_premium?: boolean;
}

export interface SearchWork {
  id: string;
  title: string;
  slug: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  source_url: string | null;
  video_thumbnail_url: string | null;
  width: number;
  height: number;
  asset_type: "image" | "video";
  similarity: number;
}

export function useSearch() {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<SearchUser[]>([]);
  const [works, setWorks] = useState<SearchWork[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setUsers([]);
      setWorks([]);
      return;
    }

    const fetchResults = async () => {
      try {
        // Cancel previous request if it exists
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        
        // Create a new abort controller
        abortControllerRef.current = new AbortController();
        
        setIsLoading(true);

        // Fetch users and works in parallel
        // Limit to 5 results per category for dropdown
        const [usersResponse, worksResponse] = await Promise.all([
          supabase.rpc('search_users', { 
            search_term: debouncedQuery,
            max_results: 5
          }),
          supabase.rpc('search_works', { 
            search_term: debouncedQuery,
            max_results: 5
          })
        ]);

        if (usersResponse.error) throw usersResponse.error;
        if (worksResponse.error) throw worksResponse.error;

        setUsers(usersResponse.data || []);
        setWorks(worksResponse.data || []);
      } catch (error) {
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Search error:', error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchResults();

    // Clean up function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [debouncedQuery]);

  const clearSearch = () => {
    setQuery("");
    setUsers([]);
    setWorks([]);
  };

  return {
    query,
    setQuery,
    users,
    works,
    isLoading,
    isOpen,
    setIsOpen,
    clearSearch,
    hasResults: users.length > 0 || works.length > 0
  };
} 